import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import ProbabilityOutcomeTable from "../../../../components/ui/ProbabilityOutcomeTable";

const SUMS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function countSum(sum) {
  let count = 0;
  for (let a = 1; a <= 6; a += 1) {
    for (let b = 1; b <= 6; b += 1) {
      if (a + b === sum) count += 1;
    }
  }
  return count;
}

function fractionLatex(num, den = 36) {
  return `\\frac{${num}}{${den}}`;
}

function buildWrongNumerators(correct, targetSum) {
  const candidates = [
    targetSum,
    correct + 1,
    correct - 1,
    countSum(targetSum - 1),
    countSum(targetSum + 1),
    11,
    6,
    5,
    4,
    3,
    2,
    1,
  ].filter((x) => x >= 1 && x <= 12 && x !== correct);

  const seen = new Set();
  const unique = [];
  for (const x of candidates) {
    if (seen.has(x)) continue;
    seen.add(x);
    unique.push(x);
    if (unique.length >= 3) break;
  }

  let n = 1;
  while (unique.length < 3) {
    if (n !== correct && !seen.has(n)) {
      unique.push(n);
      seen.add(n);
    }
    n += 1;
  }

  return unique.slice(0, 3);
}

function DiceSumTable({ targetSum }) {
  return (
    <ProbabilityOutcomeTable
      rows={[1, 2, 3, 4, 5, 6]}
      columns={[1, 2, 3, 4, 5, 6]}
      getCellValue={(firstDie, secondDie) => firstDie + secondDie}
      isHighlighted={(firstDie, secondDie) => firstDie + secondDie === targetSum}
      ariaLabel={`Tabela sum oczek równych ${targetSum}`}
    />
  );
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 28:
 *   dwa rzuty kostką, suma = 11.  Sprzyjające: (5,6),(6,5) → P = 2/36  (D).
 */
function createMay2025OriginalTask() {
  const targetSum = 11;
  const favCount = countSum(targetSum);
  const options = [1, 6, 11, 2].map((numerator, index) => ({
    numerator,
    latex: `\\frac{${numerator}}{36}`,
    isCorrect: numerator === favCount,
    label: String.fromCharCode(65 + index),
  }));

  return {
    type: "MaturaTwoDiceSumEqualsClosedTask",
    data: {
      targetSum,
      favCount,
      options,
      correctIndex: options.findIndex((option) => option.isCorrect),
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 28,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return createMay2025OriginalTask();
  }

  const targetSum = pickOne(SUMS);
  const favCount = countSum(targetSum);
  const correctLatex = fractionLatex(favCount);

  const rawOptions = [
    { numerator: favCount, latex: correctLatex, isCorrect: true },
    ...buildWrongNumerators(favCount, targetSum).map((n) => ({
      numerator: n,
      latex: fractionLatex(n),
      isCorrect: false,
    })),
  ];

  const options = shuffle(rawOptions).map((opt, idx) => ({
    ...opt,
    label: String.fromCharCode(65 + idx),
  }));
  const correctIndex = options.findIndex((opt) => opt.isCorrect);

  return {
    type: "MaturaTwoDiceSumEqualsClosedTask",
    data: {
      targetSum,
      favCount,
      options,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { targetSum, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Doświadczenie losowe polega na dwukrotnym rzucie symetryczną
        sześcienną kostką do gry, która na każdej ściance ma inną liczbę
        oczek - od jednego oczka do sześciu oczek.
      </p>

      <p>
        Zdarzenie <InlineMath math={"A"} /> polega na tym, że suma liczb
        wyrzuconych oczek będzie równa <InlineMath math={String(targetSum)} />.
      </p>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>

      <p>
        Prawdopodobieństwo zdarzenia <InlineMath math={"A"} /> jest równe
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    !!userAnswer &&
    typeof userAnswer.choiceIndex === "number" &&
    userAnswer.choiceIndex === data.correctIndex
  );
}

export function renderAnswer({ data }) {
  const correct = data.options?.[data.correctIndex];
  if (!correct) return null;
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />;
}

export function renderExplanation({ data }) {
  const { targetSum, favCount } = data;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1. Liczymy wszystkie możliwe wyniki.</strong>
      </p>
      <p>
        Wykonujemy dwa rzuty kostką, więc wynik zapisujemy jako uporządkowaną
        parę <InlineMath math={"(a,b)"} />. Wszystkich wyników jest
        <InlineMath math={"6\\cdot 6=36"} />.
      </p>

      <p>
        <strong>Krok 2. Zaznaczamy wyniki sprzyjające.</strong>
      </p>
      <p>
        W tabeli wpisujemy sumę oczek dla każdej pary wyników i zaznaczamy te
        pola, w których suma jest równa <InlineMath math={String(targetSum)} />.
      </p>

      <DiceSumTable targetSum={targetSum} />

      <p>
        Z tabeli odczytujemy <InlineMath math={String(favCount)} /> wyników
        sprzyjających, czyli <InlineMath math={`|A|=${favCount}`} />.
      </p>

      <p>
        <strong>Krok 3. Obliczamy prawdopodobieństwo.</strong>
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`P(A)=\\frac{|A|}{|\\Omega|}=\\frac{${favCount}}{36}`} />
      </div>
    </div>
  );
}
